import { FunctionComponent, useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { ContentBrick } from '../../../../../app-kit/components/ContentBrick/ContentBrick';
import { Bar, BarChart, Cell, LabelList, XAxis, YAxis } from 'recharts';
import { DemographicsLegend, LegendEntry } from '../DemographicsLegend';
import { spacingHelper } from '@property.com.au/ui-kit';
import { SubBrickTitle } from '../SubBrickTitle';
import { ColorVariant, mapColorVariantToBarStyle } from '../../../../../helpers/colorVariant';

const CHART_DIMENSIONS: { height: number; width: number } = {
  height: 110,
  width: 160,
};

type BarGraphBar = {
  label: string;
  value: number;
  colorVariant: ColorVariant;
};

type Props = {
  title: string;
  subtitle: string;
  legendEntries: LegendEntry[];
  barGraphBars: BarGraphBar[];
  className?: string;
};

const Content = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-area: age;
`;

const ChartContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  justify-self: end;
  margin-bottom: ${spacingHelper('extraSmall')};
  width: ${CHART_DIMENSIONS.width}px;
  height: ${CHART_DIMENSIONS.height}px;
`;

const MINIMUM_BAR_HEIGHT_SHARE = 0.26;

export const AgeGroupsSubBrick: FunctionComponent<Props> = ({
  title,
  subtitle,
  legendEntries,
  barGraphBars,
  className,
}: Props) => {
  const theme = useTheme();

  const [isClient, setIsClient] = useState(false);

  const tallestBarValue = Math.max(...barGraphBars.map((bar) => bar.value));

  const barChartData = barGraphBars.map((barGraphBar) => ({
    name: barGraphBar.label,
    // The tallest bar takes 100% of available height. The other bars are scaled proportionally.
    // Therefore, the minimum bar height needs to specified as a percentage of the tallest bar height.
    value: Math.max(barGraphBar.value, tallestBarValue * MINIMUM_BAR_HEIGHT_SHARE),
    style: mapColorVariantToBarStyle(barGraphBar.colorVariant, theme),
  }));

  useEffect(() => {
    setIsClient(true);
  }, []);

  return (
    <ContentBrick
      titleComponent={<SubBrickTitle as="h3">{title}</SubBrickTitle>}
      subtitle={subtitle}
      isSubBrick={true}
      className={className}
    >
      <Content>
        <DemographicsLegend legendEntries={legendEntries} />
        <ChartContainer>
          {isClient && (
            <BarChart
              width={CHART_DIMENSIONS.width}
              height={CHART_DIMENSIONS.height}
              data={barChartData}
              margin={{ bottom: 0, top: 0, left: -4, right: -4 }}
            >
              <YAxis hide type="number" domain={[0, tallestBarValue]} />
              <XAxis hide padding={{ left: 0, right: 0 }} />
              <Bar dataKey="value" radius={3}>
                {barChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={barChartData[index].style.barColor} />
                ))}
                <LabelList
                  dataKey="name"
                  // Custom label implementation required to control the colour of the label
                  content={({ x, y, width, name, index }) => {
                    if (typeof index !== 'number') return null;
                    const xValue = Number(x);
                    const yValue = Number(y);
                    const widthValue = Number(width);
                    const fill = barChartData[index].style.barLabelColor;

                    return (
                      <g>
                        <text
                          x={xValue + widthValue / 2}
                          y={yValue + 16}
                          fill={fill}
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          {name}
                        </text>
                      </g>
                    );
                  }}
                />
              </Bar>
            </BarChart>
          )}
        </ChartContainer>
      </Content>
    </ContentBrick>
  );
};
